// Shared onboarding option config. Kept in one place so the wizard, the sourcing
// explanation, and (later) settings all speak the same vocabulary.

export type SellTo = 'consumers' | 'local_business' | 'b2b' | 'government';
export type GeoScope = 'hyperlocal' | 'regional' | 'national' | 'none';

// Labels describe the BUYER you're trying to reach, NOT your own business.
// (QA P1: the old "Local businesses (HVAC, cafes, salons)" hint described the
// seller and lured a homeowner-serving HVAC contractor into the wrong branch.)
export const SELL_TO_OPTIONS: { value: SellTo; label: string; hint: string }[] = [
  { value: 'consumers', label: 'Individual people', hint: 'Homeowners, buyers, families — e.g. a realtor, or a contractor serving households.' },
  { value: 'local_business', label: 'Nearby businesses', hint: "You sell TO local shops/offices — e.g. you service restaurants or supply salons." },
  { value: 'b2b', label: 'Companies (B2B)', hint: 'Businesses you reach by size, industry, and buying signals — not by walking in.' },
  { value: 'government', label: 'Government / public sector', hint: 'Agencies, municipalities, public contracts.' },
];

export const GEO_OPTIONS: { value: GeoScope; label: string; hint: string }[] = [
  { value: 'hyperlocal', label: 'Hyper-local', hint: 'A city or a radius around you.' },
  { value: 'regional', label: 'Regional', hint: 'A state or multi-city area.' },
  { value: 'national', label: 'National', hint: 'Anywhere in the U.S.' },
  { value: 'none', label: 'No constraint', hint: "Location truly doesn't matter (incl. global)." },
];

export const COMPANY_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];

// The signal checklist (step 5). `detailable` signals prompt for specifics
// (which title? which software?) — QA P3: an EOS firm needs to say the job title
// is "Integrator," not just "a job was posted." That specific text is captured in
// the profile's signalNotes and is what turns a category into a usable trigger.
// Consumer signals carry a `group` so the wizard can split them by seller type —
// a landscaper (home-services) shouldn't be shown pre-foreclosure/probate/tax-
// delinquent alongside their real signals (Phase 8 Tier 2: one flat list read as
// irrelevant and slightly alarming). `group` is only meaningful for consumers.
export type ConsumerSignalGroup = 'realestate' | 'homeservices';
export const PROSPECT_SIGNALS: { key: string; label: string; appliesTo: SellTo[]; detailable?: boolean; group?: ConsumerSignalGroup }[] = [
  // B2B / local / gov
  { key: 'recently_hired_role', label: 'Recently hired for a specific role', appliesTo: ['b2b', 'local_business', 'government'], detailable: true },
  { key: 'opened_location', label: 'Opened or expanded a location', appliesTo: ['b2b', 'local_business'] },
  { key: 'posted_job_title', label: 'Posted a job for a specific title', appliesTo: ['b2b', 'government'], detailable: true },
  { key: 'uses_software', label: 'Uses specific software / tech', appliesTo: ['b2b'], detailable: true },
  { key: 'recently_funded', label: 'Recently funded or acquired', appliesTo: ['b2b'] },
  { key: 'holds_certification', label: 'Holds a specific certification / license', appliesTo: ['b2b', 'local_business'], detailable: true },
  { key: 'leadership_change', label: 'Leadership / ownership change', appliesTo: ['b2b', 'government'] },
  { key: 'new_facility_permit', label: 'Filed a new-construction / facility permit', appliesTo: ['b2b', 'government'] },
  // Consumer — real-estate seller signals (QA P5: sellers, not buyers)
  { key: 'long_tenure', label: 'Long tenure in current home', appliesTo: ['consumers'], group: 'realestate' },
  { key: 'high_equity', label: 'High home equity', appliesTo: ['consumers'], group: 'realestate' },
  { key: 'expired_listing', label: 'Expired / withdrawn listing', appliesTo: ['consumers'], group: 'realestate' },
  { key: 'absentee_owner', label: 'Absentee / out-of-state owner', appliesTo: ['consumers'], group: 'realestate' },
  { key: 'pre_foreclosure', label: 'Pre-foreclosure / tax-delinquent', appliesTo: ['consumers'], group: 'realestate' },
  { key: 'probate', label: 'Probate / inherited property', appliesTo: ['consumers'], group: 'realestate' },
  { key: 'life_change', label: 'Divorce / downsizing / relocation', appliesTo: ['consumers'], group: 'realestate' },
  // Consumer — home-services signals (QA P1: HVAC etc. serving households)
  { key: 'home_age', label: 'Older home (aging systems)', appliesTo: ['consumers'], detailable: true, group: 'homeservices' },
  { key: 'no_recent_permit', label: 'No recent permit for the work you do', appliesTo: ['consumers'], group: 'homeservices' },
];

export const CONSUMER_SIGNAL_GROUPS: { group: ConsumerSignalGroup; label: string }[] = [
  { group: 'realestate', label: 'Property / real-estate signals (selling or investing)' },
  { group: 'homeservices', label: 'Home-services signals (property condition)' },
];

export const CHANNELS: { key: string; label: string; hint: string; fieldOnly?: boolean }[] = [
  { key: 'email', label: 'Email', hint: 'Tailored cold email + reply handling.' },
  { key: 'calls', label: 'Calls', hint: 'Phone prospecting dashboard.' },
  { key: 'walkin', label: 'Walk-in visits', hint: 'In-person, route-planned field visits.', fieldOnly: true },
  { key: 'content', label: 'Content', hint: 'Brand voice + content calendar.' },
];

// Whether walk-in is RECOMMENDED (a physical field motion fits). We no longer
// hard-disable it on geo alone — QA P2 showed a regional/national B2B with a real
// field team would wrongly lose the channel. Instead it's always selectable;
// when not recommended we default it off and explain, rather than kill it.
export function walkinRecommended(sellTo: SellTo | null, geo: GeoScope | null): boolean {
  if (sellTo === 'local_business') return true;
  if (geo === 'hyperlocal' || geo === 'regional') return true;
  return false;
}
